// Supported CMS backends — one entry per `cms_connections.cms_type` the
// backend accepts (see backend/services/cms/factory.py). Drives the CMS
// Integrations list and each integration's guide page.

export const CMS_CATALOG = [
  {
    id: "astro",
    name: "Astro",
    via: "via GitHub",
    tagline: "Markdown/MDX content collections committed to a GitHub repo.",
    mark: "A",
    color: "#BC52EE",
    bg: "linear-gradient(135deg, #17191E 0%, #3245FF 60%, #BC52EE 100%)",
    docsUrl: "https://docs.astro.build/en/guides/content-collections/",
    capabilities: [
      "Syncs every .md/.mdx file in the content dir (plus one level of locale folders)",
      "Edits commit straight to your branch — the live site updates on your next deploy",
      "Publish / unpublish flips the draft frontmatter key",
      "New drafts are created as files in the content dir",
    ],
    limitations: [
      "Files with custom MDX components can't be edited here (edit them in GitHub)",
      "Only one level of subfolders is read",
    ],
    steps: [
      {
        title: "Keep posts in a content collection",
        body: "Put each article in its own .md or .mdx file with YAML frontmatter, under one folder such as src/content/blog. Locale subfolders (src/content/blog/en/, …/id/) are supported.",
        code: "---\ntitle: How to pick a CRM\ndescription: A short summary for search results\npubDate: 2026-01-15\ndraft: false\n---\n\nYour article body in markdown…",
      },
      {
        title: "Create a fine-grained GitHub token",
        body: "GitHub → Settings → Developer settings → Fine-grained tokens → Generate new token. Under Repository access pick only this site's repo. Under Permissions set Contents to Read and write.",
        link: { href: "https://github.com/settings/personal-access-tokens/new", label: "Create a token on GitHub" },
      },
      {
        title: "Connect the repo below",
        body: "Enter the repo owner and name, the branch your site deploys from, the content folder, and the token. Click Test Connection — it should report how many content files it found.",
      },
      {
        title: "Set live URLs, then sync",
        body: "In Site & Sync, set your Live Site URL and URL Path Prefix (e.g. \"blog\" for /blog/slug) so GSC data matches your pages. Then run Full Sync from Overview.",
        sitePrompt: true,
      },
    ],
  },
  {
    id: "webflow",
    name: "Webflow",
    via: "via Data API",
    tagline: "Items from one Webflow CMS collection, read and written through the Webflow API.",
    mark: "W",
    color: "#146EF5",
    bg: "#146EF5",
    docsUrl: "https://developers.webflow.com/data/docs/working-with-the-cms",
    capabilities: [
      "Syncs every non-archived item in the chosen collection",
      "Body, description and publish-date fields are detected automatically",
      "Commercial collections (Services, Products…) feed the sitemap as product pages",
      "Edits to live items are published right away; drafts stay drafts",
      "Publish / unpublish controls the item's live state",
      "New drafts are created as draft items in the collection",
    ],
    limitations: [
      "Rich text containing Webflow components can't be edited here",
      "Only the primary locale and one collection are synced",
      "The page H1 is usually the Name field — edit the title to change it",
    ],
    steps: [
      {
        title: "Connect with Webflow",
        body: "Click Connect with Webflow below. Webflow asks you to pick the workspace or sites to share, then sends you back here. No token or IDs to copy.",
        oauth: true,
      },
      {
        title: "Confirm what each collection is",
        body: "We list your collections and suggest a role for each: Articles for the blog collection (it needs a Rich Text body), Commercial pages for things like Services or Products, Ignore for the rest (Authors, Categories…). Adjust if needed and save.",
      },
      {
        title: "Test the connection",
        body: "Test Connection reports the article count and which fields are used for the body and meta description. Webflow's blog template fields (post-body, meta-description) are picked up automatically.",
      },
      {
        title: "Set live URLs, then sync",
        body: "In Site & Sync, set your Live Site URL (custom domain or the .webflow.io address). Leave URL Path Prefix blank to use each collection's slug. Then run Full Sync from Overview — commercial pages come in with the Visual Sitemap crawl.",
        sitePrompt: true,
      },
    ],
  },
];

export const cmsById = id => CMS_CATALOG.find(c => c.id === id);

// `conn` is GET /cms/connection — one connection per org, so only the
// connected CMS ever shows anything but "Not connected".
export function cmsStatus(cms, conn) {
  if (!conn || conn.cms_type !== cms.id) return { key: "none", label: "Not connected", cls: "bg-white text-zinc-400 border-zinc-100" };
  if (!conn.has_secret) return { key: "setup", label: "Needs credentials", cls: "bg-amber-50 text-amber-700 border-amber-200" };
  if (conn.status === "error") return { key: "error", label: "Connection error", cls: "bg-red-50 text-red-600 border-red-200" };
  if (conn.status === "ok") return { key: "active", label: "Connected", cls: "bg-green-50 text-green-700 border-green-200" };
  return { key: "active", label: "Connected · not tested", cls: "bg-green-50 text-green-700 border-green-200" };
}

export function CmsLogo({ cms, size = 40 }) {
  return (
    <div
      className="flex items-center justify-center rounded-xl text-white font-black flex-shrink-0 shadow-sm"
      style={{ width: size, height: size, background: cms.bg, fontSize: size * 0.45 }}
      aria-hidden="true"
    >
      {cms.mark}
    </div>
  );
}

export function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold border rounded-full ${status.cls}`}>
      {status.label}
    </span>
  );
}
